/**
 * IconButton — circular pressable button that renders an icon with a spring
 * press animation.
 *
 * Accepts the icon as a React.ElementType so it stays decoupled from any
 * specific icon library. The alarm control widget passes lock/warning icons.
 */
import { type FC, memo, useCallback, useMemo } from "react";
import { type GestureResponderEvent, Platform, Pressable, type StyleProp, type ViewStyle } from "react-native";
import "../../theme"; // module augmentation for react-native-unistyles types
import { StyleSheet } from "react-native-unistyles";
import Animated, { useAnimatedStyle, useSharedValue, withSpring } from "react-native-reanimated";

// ---------------------------------------------------------------------------
// Size configuration
// ---------------------------------------------------------------------------

const BUTTON_SIZE = {
  default: 48,
  sm: 32,
} as const;

type IconButtonSize = keyof typeof BUTTON_SIZE;

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

export interface IconButtonProps {
  /** The icon component to render inside the button. */
  readonly Icon: React.ElementType;
  /** Accessible title — required for screen readers. */
  readonly title: string;
  readonly onPress?: (event: GestureResponderEvent) => void;
  readonly elevation?: boolean;
  readonly size?: IconButtonSize;
  readonly style?: StyleProp<ViewStyle>;
  readonly animatedStyle?: StyleProp<ViewStyle>;
  readonly buttonStyle?: StyleProp<ViewStyle>;
  readonly disabled?: boolean;
  readonly testID?: string;
}

// ---------------------------------------------------------------------------
// Hook: spring scale animation on press
// ---------------------------------------------------------------------------

export function useTransformScaleOnPress(props?: { readonly scale?: number }) {
  const scale = useSharedValue(1);

  const transform = useAnimatedStyle(
    () => ({
      transform: [{ scale: scale.value }],
    }),
    [scale],
  );

  const onPressIn = useCallback(() => {
    scale.value = withSpring(props?.scale ?? 0.92, {
      damping: 30,
      stiffness: 700,
    });
  }, [props?.scale, scale]);

  const onPressOut = useCallback(() => {
    scale.value = withSpring(1, {
      damping: 30,
      stiffness: 400,
    });
  }, [scale]);

  return { transform, onPressIn, onPressOut };
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const IconButton: FC<IconButtonProps> = memo(
  ({
    Icon,
    title,
    onPress,
    style,
    elevation = false,
    size = "default",
    buttonStyle,
    animatedStyle,
    disabled,
    testID,
  }) => {
    const buttonSize = useMemo(() => BUTTON_SIZE[size], [size]);

    const { transform, onPressIn, onPressOut } = useTransformScaleOnPress();

    return (
      <Pressable
        onPress={onPress}
        onPressIn={onPressIn}
        onPressOut={onPressOut}
        disabled={disabled}
        role="button"
        accessibilityHint=""
        accessibilityLabel={title}
        accessibilityRole="button"
        style={[styles.pressable, { width: buttonSize, height: buttonSize }, style]}
        testID={testID}
      >
        <Animated.View
          style={[
            styles.button,
            elevation ? styles.elevated : undefined,
            { width: buttonSize, height: buttonSize },
            buttonStyle,
            animatedStyle,
            transform,
          ]}
        >
          <Icon />
        </Animated.View>
      </Pressable>
    );
  },
);

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const styles = StyleSheet.create((theme) => ({
  pressable: {
    // Base pressable — intentionally empty for now
  },
  button: {
    backgroundColor: theme.colors.interactive.buttons.background_icon_button,
    borderWidth: 1,
    borderColor: theme.colors.interactive.buttons.background_icon_button,
    justifyContent: "center",
    alignItems: "center",
    borderRadius: 999,
    ...Platform.select({
      web: {
        willChange: "transform",
      },
    }),
  },
  elevated: {
    elevation: 2,
    shadowOpacity: 0.1,
    shadowRadius: 2,
    shadowOffset: { width: 0, height: 1 },
  },
}));
